import { addMoneyCents, assertBetCents, linePayoutCents, multiplyMoneyCents } from './money';
import { PAYLINES } from './paylines';
import { ACTIVE_PAYOUTS, type PayoutTable } from './payout-tables';
import { REGULAR_SYMBOL_IDS } from './symbols';
import type {
  Grid,
  GridPosition,
  MoneyCents,
  RegularSymbolId,
  SymbolId,
} from './types';

export type PayingSymbolId = RegularSymbolId | 'WILD';
export type MatchCount = 3 | 4 | 5;

export interface PaylineEvaluation {
  readonly symbolId: PayingSymbolId;
  readonly count: MatchCount;
  readonly payoutCredits: number;
}

export interface LineWin extends PaylineEvaluation {
  readonly lineIndex: number;
  readonly amountCents: MoneyCents;
  readonly multiplier: number;
  readonly positions: readonly GridPosition[];
}

export interface WildMultiplierPosition extends GridPosition {
  readonly multiplier?: number;
}

export interface WinEvaluation {
  readonly lineWins: readonly LineWin[];
  readonly scatterCount: number;
  readonly totalWinCents: MoneyCents;
}

export type PaylineSymbols = readonly [SymbolId, SymbolId, SymbolId, SymbolId, SymbolId];

export function evaluatePaylineSymbolIds(
  symbolIds: PaylineSymbols,
  payouts: PayoutTable = ACTIVE_PAYOUTS,
): PaylineEvaluation | null {
  if (symbolIds.every((symbolId) => symbolId === 'WILD')) {
    const payoutCredits = payouts.WILD[5] ?? 0;
    return payoutCredits > 0
      ? Object.freeze({ symbolId: 'WILD', count: 5, payoutCredits })
      : null;
  }

  let best: PaylineEvaluation | null = null;
  REGULAR_SYMBOL_IDS.forEach((candidate) => {
    let rawCount = 0;
    for (const visibleId of symbolIds) {
      if (visibleId === candidate || visibleId === 'WILD') rawCount += 1;
      else break;
    }
    if (rawCount < 3) return;
    const count = Math.min(rawCount, 5) as MatchCount;
    const payoutCredits = payouts[candidate][count] ?? 0;
    if (payoutCredits <= 0) return;
    if (
      best === null
      || payoutCredits > best.payoutCredits
      || (payoutCredits === best.payoutCredits && count > best.count)
    ) {
      best = Object.freeze({ symbolId: candidate, count, payoutCredits });
    }
  });
  return best;
}

export function getPaylineSymbolIds(
  grid: Grid<SymbolId>,
  rows: readonly [number, number, number, number, number],
): PaylineSymbols {
  const symbols = rows.map((row, reel) => grid[reel]?.[row]);
  if (symbols.some((symbol) => symbol === undefined)) {
    throw new RangeError('Die Gewinnlinie verweist auf eine ungültige Rasterposition.');
  }
  return symbols as unknown as PaylineSymbols;
}

export function countScatters(grid: Grid<SymbolId>): number {
  return grid.reduce(
    (total, reel) => total + reel.filter((symbol) => symbol === 'SCATTER').length,
    0,
  );
}

export function evaluateWins(
  grid: Grid<SymbolId>,
  betCents: MoneyCents,
  payouts: PayoutTable = ACTIVE_PAYOUTS,
  wildMultipliers: readonly WildMultiplierPosition[] = [],
): WinEvaluation {
  assertBetCents(betCents);
  const lineWins: LineWin[] = [];
  PAYLINES.forEach((rows, lineIndex) => {
    const evaluation = evaluatePaylineSymbolIds(getPaylineSymbolIds(grid, rows), payouts);
    if (evaluation === null) return;
    const positions = Object.freeze(Array.from(
      { length: evaluation.count },
      (_unused, reel) => Object.freeze({ reel, row: rows[reel] }) as GridPosition,
    ));
    const multiplier = positions.reduce((total, position) => {
      const wild = wildMultipliers.find((entry) => (
        entry.reel === position.reel && entry.row === position.row
      ));
      if (wild === undefined || grid[position.reel]?.[position.row] !== 'WILD') return total;
      const value = wild.multiplier ?? 1;
      if (!Number.isSafeInteger(value) || value < 1) {
        throw new RangeError('Wild-Multiplikatoren müssen positive sichere Ganzzahlen sein.');
      }
      return total + value - 1;
    }, 1);
    if (!Number.isSafeInteger(multiplier)) {
      throw new RangeError('Der kombinierte Linienmultiplikator ist zu groß.');
    }
    lineWins.push(Object.freeze({
      lineIndex,
      ...evaluation,
      amountCents: multiplyMoneyCents(
        linePayoutCents(evaluation.payoutCredits, betCents),
        multiplier,
      ),
      multiplier,
      positions,
    }));
  });
  return Object.freeze({
    lineWins: Object.freeze(lineWins),
    scatterCount: countScatters(grid),
    totalWinCents: lineWins.reduce(
      (sum, lineWin) => addMoneyCents(sum, lineWin.amountCents),
      0,
    ),
  });
}
